import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Generale",
      questions: [
        {
          q: "Cos'è iSiAP?",
          a: "iSiAP è una piattaforma digitale completa per la gestione delle attività commerciali. Permette di controllare vendite, magazzino e documenti fiscali da un'unica interfaccia, automatizzando l'invio dei corrispettivi all'Agenzia delle Entrate."
        },
        {
          q: "A chi è rivolto iSiAP?",
          a: "iSiAP è pensato per ristoranti, bar, negozi, attività commerciali, lavanderie, società sportive e qualsiasi realtà che necessita di una gestione efficiente delle vendite e della fiscalità. Dal piccolo esercente alla catena retail."
        },
        {
          q: "iSiAP sostituisce il registratore telematico?",
          a: "Sì, con iSiAP non hai più bisogno del registratore telematico tradizionale. Il sistema invia automaticamente i documenti commerciali al cassetto fiscale tramite le credenziali dell'Agenzia delle Entrate, eliminando costi di manutenzione e revisioni periodiche."
        }
      ]
    },
    {
      category: "Funzionalità",
      questions: [
        {
          q: "Quali sono le principali funzionalità di iSiAP?",
          a: "iSiAP offre: gestione vendite e cassa, controllo magazzino e forniture, gestione prenotazioni, invio automatico documenti fiscali, report e analisi in tempo reale, gestione personale e turni, programmi fedeltà e promozioni."
        },
        {
          q: "Posso gestire più punti vendita?",
          a: "Sì, iSiAP supporta la gestione multi-sede. Puoi controllare tutti i tuoi punti vendita da un'unica dashboard, con dati consolidati e report separati per ogni sede."
        },
        {
          q: "Il sistema funziona offline?",
          a: "iSiAP è progettato per funzionare anche in caso di connessione intermittente. I dati vengono sincronizzati automaticamente non appena la connessione viene ripristinata."
        },
        {
          q: "Come funziona l'invio dei corrispettivi?",
          a: "L'invio avviene in modo completamente automatico. Una volta configurate le credenziali dell'Agenzia delle Entrate, iSiAP trasmette i documenti commerciali direttamente al cassetto fiscale senza alcun intervento manuale."
        }
      ]
    },
    {
      category: "Prezzi e Supporto",
      questions: [
        {
          q: "Quanto costa iSiAP?",
          a: "I costi variano in base alle funzionalità richieste e al numero di postazioni. Contattaci per ricevere un preventivo personalizzato in base alle tue esigenze specifiche."
        },
        {
          q: "È previsto un periodo di prova?",
          a: "Sì, offriamo una demo gratuita e senza impegno. Contattaci per prenotare una dimostrazione del sistema e valutare se iSiAP è la soluzione giusta per te."
        },
        {
          q: "Che tipo di supporto offrite?",
          a: "Offriamo supporto tecnico dedicato, con assistenza disponibile 24/7 per le emergenze. Il nostro team è sempre pronto ad aiutarti con formazione, configurazione e risoluzione problemi."
        },
        {
          q: "Gli aggiornamenti sono inclusi?",
          a: "Sì, tutti gli aggiornamenti software sono inclusi nel servizio. Il sistema viene costantemente migliorato e aggiornato per garantire conformità normativa e nuove funzionalità."
        }
      ]
    },
    {
      category: "Sicurezza e Conformità",
      questions: [
        {
          q: "I miei dati sono al sicuro?",
          a: "Assolutamente sì. Utilizziamo protocolli di sicurezza avanzati, crittografia dei dati e backup automatici. I tuoi dati sono protetti secondo le normative vigenti incluso il GDPR."
        },
        {
          q: "iSiAP è conforme alla normativa fiscale italiana?",
          a: "Sì, iSiAP è pienamente conforme alla normativa italiana sui corrispettivi telematici. Il sistema viene costantemente aggiornato per adeguarsi a eventuali modifiche legislative."
        },
        {
          q: "Come avviene la migrazione dai sistemi esistenti?",
          a: "Il nostro team ti supporta in tutto il processo di migrazione. Possiamo importare i dati dai tuoi sistemi attuali e configurare iSiAP in base alle tue specifiche esigenze."
        }
      ]
    }
  ];

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  let globalIndex = 0;

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="bg-[#f5f6fa] py-16 md:py-24" data-testid="faq-hero">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge mb-6">FAQ</span>
            <h1 className="heading-1 text-[#1a1f36] mb-6">
              Domande Frequenti
            </h1>
            <p className="body-large">
              Trova le risposte alle domande più comuni su iSiAP. 
              Non trovi quello che cerchi? Contattaci direttamente.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding" data-testid="faq-content">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="icon-container">
                    <HelpCircle size={20} />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1f36]">{category.category}</h2>
                </div>
                
                <div className="space-y-4">
                  {category.questions.map((faq, qIndex) => {
                    const currentIndex = globalIndex++;
                    return (
                      <div
                        key={qIndex}
                        className="border border-[#e2e4ed] rounded-xl overflow-hidden"
                        data-testid={`faq-item-${currentIndex}`}
                      >
                        <button
                          onClick={() => toggleFaq(currentIndex)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-[#f5f6fa] transition-colors"
                        >
                          <span className="font-semibold text-[#1a1f36] pr-4">{faq.q}</span>
                          <ChevronDown
                            size={20}
                            className={`text-[#FF6B00] shrink-0 transition-transform duration-300 ${
                              openIndex === currentIndex ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {openIndex === currentIndex && (
                          <div className="px-6 pb-6 pt-0">
                            <p className="text-[#4a5068] leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1f36] py-16" data-testid="faq-cta">
        <div className="section-container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Non hai trovato la risposta che cercavi?
            </h2>
            <p className="text-[#9ca3af] mb-8">
              Il nostro team è a disposizione per rispondere a tutte le tue domande.
            </p>
            <a
              href="/contatti"
              className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#E55F00] transition-colors"
              data-testid="faq-contact-cta"
            >
              Contattaci
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;
